import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { type JiraIssueRef } from '@databases/types/confluence';
import {
  type Database,
  type DatabaseField,
  DatabaseFieldType,
} from '@databases/types/database';
import { type DatabaseCalculationType } from '@databases/types/database/view';
import {
  getReferencedJiraIssueRefs,
  getReferencedJiraIssueDetailsField,
} from '@databases/utils/database/field-types/jiraIssueDetails';
import {
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
} from '@databases/utils/database/utils';
import { loadJiraIssue } from '@databases/utils/redux/jira/actions';
import { selectJiraIssueOptional } from '@databases/utils/redux/jira/selectors';
import { useEffect, useMemo, useRef, useState } from 'react';

import { calculationRequiresFieldValues } from './quickCalcUtils';

/** Number of Jira issues to load per batch. */
const BATCH_SIZE = 100;

function isJiraIssueDetailsField(
  field: DatabaseField
): field is DatabaseField<DatabaseFieldType.JIRA_ISSUE_DETAILS> {
  return field.type === DatabaseFieldType.JIRA_ISSUE_DETAILS;
}

interface JiraRefWithColumn {
  ref: JiraIssueRef;
  column: string;
}

function collectUnloadedJiraRefs(
  database: Database,
  field: DatabaseField<DatabaseFieldType.JIRA_ISSUE_DETAILS>,
  state: DefaultRootState
): JiraRefWithColumn[] {
  const jiraField = field.settings.jiraField;
  if (!jiraField) {
    return [];
  }

  const referencedJiraField = getReferencedJiraIssueDetailsField({ database, field });
  if (!referencedJiraField) {
    return [];
  }

  const unloadedRefs: JiraRefWithColumn[] = [];
  const seenKeys = new Set<string>();

  for (const entryId of database.entries) {
    const entry = getDatabaseEntryOptional(database, entryId);
    if (!entry) {
      continue;
    }

    const issueRefs = getReferencedJiraIssueRefs({ database, field, entry }, state);

    for (const ref of issueRefs) {
      const key = `${ref.cloudId ?? ''}:${ref.serverId ?? ''}:${ref.key}`;
      if (seenKeys.has(key)) {
        continue;
      }
      seenKeys.add(key);

      const resolved = selectJiraIssueOptional(state, ref);
      const hasColumn = resolved?.columns[jiraField] !== undefined;

      if (!hasColumn) {
        unloadedRefs.push({ ref, column: jiraField });
      }
    }
  }

  return unloadedRefs;
}

interface LoadDependenciesParams {
  database: Database;
  field: DatabaseField<DatabaseFieldType.JIRA_ISSUE_DETAILS>;
  store: ReturnType<typeof defaultStoreUtils.useStore>;
  dispatch: ReturnType<typeof defaultStoreUtils.useDispatch>;
  onProgress: (loaded: number, total: number) => void;
  isCancelled: () => boolean;
}

async function loadAllJiraDependencies({
  database,
  field,
  store,
  dispatch,
  onProgress,
  isCancelled,
}: LoadDependenciesParams): Promise<boolean> {
  const state = store.getState() as DefaultRootState;
  const unloadedRefs = collectUnloadedJiraRefs(database, field, state);

  if (unloadedRefs.length === 0) {
    return false;
  }

  onProgress(0, unloadedRefs.length);

  for (let i = 0; i < unloadedRefs.length; i += BATCH_SIZE) {
    if (isCancelled()) {
      return false;
    }

    const batch = unloadedRefs.slice(i, i + BATCH_SIZE);

    await Promise.all(
      batch.map(({ ref, column }) => dispatch(loadJiraIssue(ref, { columns: [column] })))
    );

    if (isCancelled()) {
      return false;
    }

    onProgress(Math.min(i + BATCH_SIZE, unloadedRefs.length), unloadedRefs.length);
  }

  return true;
}

/**
 * Eagerly loads Jira issue data for calculations on Jira detail fields that require field values.
 *
 * Jira field data is normally lazy-loaded as entries scroll into view, which can cause
 * calculations to show incorrect or changing results. This hook ensures all required Jira data
 * is loaded before the calculation is displayed.
 *
 * Notes about this hook:
 * - Only activates for Jira detail fields with calculations that require field values
 * - Loads data in batches to avoid overwhelming the network
 * - Re-triggers when visible entries change (e.g. filter applied/removed)
 * - Returns loading state so the UI can show a spinner
 *
 * @param database - The database (with view filters already applied)
 * @param fieldId - The field to load dependencies for
 * @param calculationType - The current calculation type (sum, average, etc.)
 * @returns Loading state and optional progress information
 */
export function useLoadJiraFieldDependencies(
  database: Database,
  fieldId: string,
  calculationType: DatabaseCalculationType | undefined
): {
  isLoading: boolean;
  progress: { loaded: number; total: number } | null;
} {
  const { entries } = database;
  const dispatch = defaultStoreUtils.useDispatch();
  const store = defaultStoreUtils.useStore();
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState<{
    loaded: number;
    total: number;
  } | null>(null);
  const loadedKeyRef = useRef<string | null>(null);

  const field = useMemo(
    () => getDatabaseFieldOptional(database, fieldId),
    [database, fieldId]
  );

  const shouldLoad = useMemo(() => {
    if (!field || !calculationType) {
      return false;
    }

    if (!isJiraIssueDetailsField(field)) {
      return false;
    }

    if (!calculationRequiresFieldValues(calculationType)) {
      return false;
    }

    return true;
  }, [field, calculationType]);

  // Fingerprint to detect when visible entries change (e.g. filter applied/removed),
  // and may require additional Jira dependencies to be loaded.
  const entriesFingerprint = `${entries.length}-${entries[0] ?? ''}-${
    entries[entries.length - 1] ?? ''
  }`;
  const loadKey = shouldLoad
    ? `${fieldId}-${calculationType}-${entriesFingerprint}`
    : null;

  useEffect(() => {
    // Preconditions for loading dependencies
    if (!field || !shouldLoad || !loadKey || !isJiraIssueDetailsField(field)) {
      return;
    }

    // Check whether we've already loaded the dependencies for this key
    if (loadedKeyRef.current === loadKey) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);

      const didLoad = await loadAllJiraDependencies({
        database,
        field,
        store,
        dispatch,
        onProgress: (loaded, total) => {
          if (!cancelled) {
            setProgress({ loaded, total });
          }
        },
        isCancelled: () => cancelled,
      });

      if (cancelled) {
        return;
      }

      if (didLoad) {
        loadedKeyRef.current = loadKey;
      }
      setProgress(null);
      setIsLoading(false);
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [database, dispatch, field, loadKey, shouldLoad, store]);

  return { isLoading: shouldLoad && isLoading, progress };
}
